import { useCallback, useEffect, useMemo, useState } from 'react';
import { useRouter } from '@tanstack/react-router';
import type { LucideIcon } from 'lucide-react';
import { Clock } from 'lucide-react';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/atoms/command';
import { apiGet, operationsClient } from '@/lib/api/client';
import { globalSearchEndpoints } from '@/lib/api/endpoints';
import {
  useAuthFieldMasks,
  useAuthPermissions,
  useAuthTechnicianId,
  useAuthPermissionScopes,
  useAuthTenantId,
} from '@/stores/auth.store';
import { useTenantId } from '@/stores/tenant.store';
import { removeLegacyKey, tenantScopedKey } from '@/lib/tenantStorage';
import { useCostingSettings } from '@/hooks/costing/useCostingSettings';
import { useFeatureFlags } from '@/stores/tenant.store';
import { useActiveModal, useUiActions } from '@/stores/ui.store';
import {
  NAV_SECTIONS,
  PINNED_NAV_ITEMS,
  SETTINGS_ITEM,
  isNavItemVisible,
  type NavItem,
} from './nav-config';
import {
  getPermittedSearchKinds,
  SEARCH_GROUPS,
  type SearchGroupDefinition,
  type SearchKind,
} from './command-palette-search';

const SEARCH_LIMIT = 5;
const SEARCH_DEBOUNCE_MS = 250;
const RECENTS_KEY = 'notifi.commandPalette.recents';
const RECENT_LIMIT = 8;

interface PaletteRecord {
  id: string;
  kind: SearchKind;
  group: string;
  label: string;
  meta?: string;
  path: string;
  icon: LucideIcon;
}

type StoredPaletteRecord = Omit<PaletteRecord, 'icon'>;

interface SearchGroupState extends SearchGroupDefinition {
  items: PaletteRecord[];
}

interface GlobalSearchApiItem {
  id: string;
  label: string;
  meta?: string;
  path: string;
}

interface GlobalSearchApiResponse {
  query: string;
  groups: Array<{
    id: SearchKind;
    heading: string;
    items: GlobalSearchApiItem[];
  }>;
}

const ICON_BY_KIND = SEARCH_GROUPS.reduce(
  (acc, group) => {
    acc[group.id] = group.icon;
    return acc;
  },
  {} as Record<SearchKind, LucideIcon>,
);

function canShowItem(
  item: NavItem,
  permissions: string[],
  technicianId: string | null,
  costingReportingEnabled: boolean,
  flags: Record<string, boolean>,
  fieldMasks: readonly string[],
  permissionScopes: Record<string, string>,
) {
  return isNavItemVisible(item, permissions, {
    technicianId,
    costingReportingEnabled,
    flags,
    fieldMasks,
    permissionScopes,
  });
}

function joinMeta(parts: Array<string | number | null | undefined>) {
  return parts
    .map((part) => (part === null || part === undefined ? '' : String(part).trim()))
    .filter(Boolean)
    .join(' · ');
}

function emptyGroups(groups: SearchGroupDefinition[] = SEARCH_GROUPS): SearchGroupState[] {
  return groups.map((group) => ({ ...group, items: [] }));
}

function readRecents(tenantId: string | null): PaletteRecord[] {
  if (typeof window === 'undefined') return [];
  try {
    const parsed = JSON.parse(
      window.localStorage.getItem(tenantScopedKey(RECENTS_KEY, tenantId)) ?? '[]',
    ) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((item): item is StoredPaletteRecord => {
        if (!item || typeof item !== 'object') return false;
        const record = item as Partial<StoredPaletteRecord>;
        return (
          typeof record.id === 'string' &&
          typeof record.label === 'string' &&
          typeof record.path === 'string' &&
          typeof record.group === 'string' &&
          typeof record.kind === 'string' &&
          record.kind in ICON_BY_KIND
        );
      })
      .slice(0, RECENT_LIMIT)
      .map((item) => ({ ...item, icon: ICON_BY_KIND[item.kind] }));
  } catch {
    return [];
  }
}

function writeRecent(record: PaletteRecord, tenantId: string | null) {
  if (typeof window === 'undefined') return;
  const { icon: _icon, ...stored } = record;
  const existing = readRecents(tenantId)
    .filter((item) => item.path !== record.path)
    .map(({ icon: _recentIcon, ...item }) => item);
  const next: StoredPaletteRecord[] = [stored, ...existing].slice(0, RECENT_LIMIT);
  window.localStorage.setItem(tenantScopedKey(RECENTS_KEY, tenantId), JSON.stringify(next));
}

function recordValue(record: PaletteRecord) {
  return joinMeta([record.group, record.label, record.meta]);
}

function navGroupsHaveContent(
  visiblePinnedItems: NavItem[],
  visibleSections: Array<{ items: NavItem[] }>,
) {
  return (
    visiblePinnedItems.length > 0 || visibleSections.some((section) => section.items.length > 0)
  );
}

async function searchRecords(
  query: string,
  permittedKinds: ReadonlySet<SearchKind>,
  signal: AbortSignal,
): Promise<SearchGroupState[]> {
  const response = await apiGet<GlobalSearchApiResponse>(
    operationsClient,
    globalSearchEndpoints.search(),
    {
      params: { q: query, limit: SEARCH_LIMIT },
      signal,
    },
  );
  const responseGroups = new Map(response.groups.map((group) => [group.id, group]));

  return SEARCH_GROUPS.filter((group) => permittedKinds.has(group.id)).map((group) => {
    const responseGroup = responseGroups.get(group.id);
    return {
      ...group,
      items: (responseGroup?.items ?? []).map((item) => ({
        ...item,
        kind: group.id,
        group: group.heading,
        icon: group.icon,
      })),
    };
  });
}

function SearchResultItem({ record, onSelect }: { record: PaletteRecord; onSelect: () => void }) {
  const Icon = record.icon;
  return (
    <CommandItem
      value={recordValue(record)}
      onSelect={onSelect}
      className="flex cursor-pointer items-center gap-2"
    >
      <Icon className="h-4 w-4 text-muted-foreground" />
      <span className="min-w-0 flex-1">
        <span className="block truncate">{record.label}</span>
        {record.meta ? (
          <span className="block truncate text-xs text-muted-foreground">{record.meta}</span>
        ) : null}
      </span>
    </CommandItem>
  );
}

export function CommandPalette() {
  const activeModal = useActiveModal();
  const { openModal, closeModal } = useUiActions();
  const router = useRouter();
  const permissions = useAuthPermissions();
  const fieldMasks = useAuthFieldMasks();
  const technicianId = useAuthTechnicianId();
  const permissionScopes = useAuthPermissionScopes();
  const costingSettings = useCostingSettings();
  const costingReportingEnabled = costingSettings.data?.enableReporting === true;
  const flags = useFeatureFlags();
  const permittedSearchKinds = useMemo(
    () => new Set(getPermittedSearchKinds(permissions)),
    [permissions],
  );
  const permittedSearchGroups = useMemo(
    () => SEARCH_GROUPS.filter((group) => permittedSearchKinds.has(group.id)),
    [permittedSearchKinds],
  );

  const isOpen = activeModal === 'commandPalette';
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchGroupState[]>(() => emptyGroups());
  const [pendingSearchGroups, setPendingSearchGroups] = useState<Set<SearchKind>>(() => new Set());
  const [searchLoading, setSearchLoading] = useState(false);
  const [recents, setRecents] = useState<PaletteRecord[]>([]);
  // Use the ACTIVE tenant (tenant store), falling back to the auth tenant —
  // mirrors getActiveTenantId() so recents key off the same tenant as the
  // util-based stores, and re-read when the active tenant switches. Both hooks
  // are called unconditionally (rules-of-hooks) and combined.
  const activeTenantId = useTenantId();
  const authTenantId = useAuthTenantId();
  const tenantId = activeTenantId ?? authTenantId;
  const trimmedQuery = query.trim();
  const searchMode = trimmedQuery.length >= 2;
  const hasSearchResults = searchResults.some((group) => group.items.length > 0);
  const visiblePinnedItems = useMemo(() => {
    const canShow = (item: NavItem) =>
      canShowItem(
        item,
        permissions,
        technicianId,
        costingReportingEnabled,
        flags,
        fieldMasks,
        permissionScopes,
      );
    // Browsing (empty query) keeps the curated top-level list. While searching,
    // NOT-567 — flatten section-parent pinned items (Schedule, Jobs) to their
    // leaf children so every page is findable by its new or old name (e.g.
    // "Service calls", "Calendar"), not just the top-level parents.
    if (!searchMode) {
      return PINNED_NAV_ITEMS.filter(canShow);
    }
    const out: NavItem[] = [];
    for (const item of PINNED_NAV_ITEMS) {
      if (item.children?.length) {
        for (const child of item.children) {
          if (canShow(child)) {
            // Carry the parent's label as a search term so the section name
            // (e.g. "schedule") still surfaces its pages once flattened.
            out.push({ ...child, aliases: [...(child.aliases ?? []), item.label] });
          }
        }
      } else if (canShow(item)) {
        out.push(item);
      }
    }
    return out;
  }, [
    searchMode,
    permissions,
    technicianId,
    costingReportingEnabled,
    flags,
    fieldMasks,
    permissionScopes,
  ]);
  const visibleSections = useMemo(
    () =>
      NAV_SECTIONS.map((section) => ({
        ...section,
        items: section.items.filter((item) =>
          canShowItem(
            item,
            permissions,
            technicianId,
            costingReportingEnabled,
            flags,
            fieldMasks,
            permissionScopes,
          ),
        ),
      })).filter((section) => section.items.length > 0),
    [permissions, technicianId, costingReportingEnabled, flags, fieldMasks, permissionScopes],
  );
  const canRenderNavigation = navGroupsHaveContent(visiblePinnedItems, visibleSections);

  useEffect(() => {
    if (!isOpen) {
      setQuery('');
      setDebouncedQuery('');
      setSearchResults(emptyGroups(permittedSearchGroups));
      setPendingSearchGroups(new Set());
      setSearchLoading(false);
      return;
    }
    setRecents(readRecents(tenantId).filter((record) => permittedSearchKinds.has(record.kind)));
  }, [isOpen, permittedSearchGroups, permittedSearchKinds, tenantId]);

  // One-time cleanup: the recents list used to live under a single global key,
  // which bled records across tenants in the same browser. Drop that legacy key
  // so its stale cross-tenant entries stop appearing; recents are now stored
  // per-tenant (see tenantScopedKey).
  useEffect(() => {
    removeLegacyKey(RECENTS_KEY);
  }, []);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedQuery(trimmedQuery);
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [trimmedQuery]);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    if (debouncedQuery.length < 2) {
      setSearchResults(emptyGroups(permittedSearchGroups));
      setPendingSearchGroups(new Set());
      setSearchLoading(false);
      return;
    }

    setSearchResults(emptyGroups(permittedSearchGroups));
    setPendingSearchGroups(new Set(permittedSearchGroups.map((group) => group.id)));
    setSearchLoading(permittedSearchGroups.length > 0);

    if (permittedSearchGroups.length > 0) {
      searchRecords(debouncedQuery, permittedSearchKinds, controller.signal)
        .then((groups) => {
          if (!cancelled) setSearchResults(groups);
        })
        .catch(() => {
          if (!cancelled) setSearchResults(emptyGroups(permittedSearchGroups));
        })
        .finally(() => {
          if (cancelled) return;
          setPendingSearchGroups(new Set());
          setSearchLoading(false);
        });
    }

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [debouncedQuery, permittedSearchGroups, permittedSearchKinds]);

  function renderSearchGroup(group: SearchGroupState) {
    if (group.items.length > 0) {
      return (
        <CommandGroup key={group.id} heading={group.heading}>
          {group.items.map((record) => (
            <SearchResultItem
              key={`${record.kind}:${record.id}`}
              record={record}
              onSelect={() => navigateRecord(record)}
            />
          ))}
        </CommandGroup>
      );
    }
    if (pendingSearchGroups.has(group.id)) {
      return (
        <CommandGroup key={group.id} heading={group.heading}>
          <CommandItem value={`Loading ${group.heading} ${trimmedQuery}`} disabled>
            <span className="h-4 w-4 rounded-full bg-muted" />
            <span className="h-4 w-40 rounded bg-muted" />
          </CommandItem>
        </CommandGroup>
      );
    }
    return null;
  }

  // Keyboard shortcut listener
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        if (isOpen) {
          closeModal();
        } else {
          openModal('commandPalette');
        }
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, openModal, closeModal]);

  const navigate = useCallback(
    (path: string) => {
      closeModal();
      router.navigate({ to: path });
    },
    [closeModal, router],
  );

  const navigateRecord = useCallback(
    (record: PaletteRecord) => {
      writeRecent(record, tenantId);
      setRecents(readRecents(tenantId).filter((recent) => permittedSearchKinds.has(recent.kind)));
      navigate(record.path);
    },
    [navigate, permittedSearchKinds, tenantId],
  );

  const renderNavigation = () => (
    <>
      {visiblePinnedItems.length > 0 && (
        <CommandGroup heading="Pages">
          {visiblePinnedItems.map((item) => {
            const Icon = item.icon;
            return (
              <CommandItem
                key={item.path}
                value={`Pages ${item.label}${item.aliases?.length ? ` ${item.aliases.join(' ')}` : ''}`}
                onSelect={() => navigate(item.path)}
                className="flex cursor-pointer items-center gap-2"
              >
                <Icon className="h-4 w-4 text-muted-foreground" />
                {item.label}
              </CommandItem>
            );
          })}
        </CommandGroup>
      )}

      {visibleSections.map((section, index) => (
        <div key={section.id}>
          {(visiblePinnedItems.length > 0 || index > 0) && <CommandSeparator />}
          <CommandGroup heading={section.label}>
            {section.items.map((item) => {
              const Icon = item.icon;
              return (
                <CommandItem
                  key={item.path}
                  value={`${section.label} ${item.label}${item.aliases?.length ? ` ${item.aliases.join(' ')}` : ''}`}
                  onSelect={() => navigate(item.path)}
                  className="flex cursor-pointer items-center gap-2"
                >
                  <Icon className="h-4 w-4 text-muted-foreground" />
                  {item.label}
                </CommandItem>
              );
            })}
          </CommandGroup>
        </div>
      ))}

      <CommandSeparator />
      <CommandGroup heading="Settings">
        <CommandItem
          value="Settings"
          onSelect={() => navigate(SETTINGS_ITEM.path)}
          className="flex cursor-pointer items-center gap-2"
        >
          <SETTINGS_ITEM.icon className="h-4 w-4 text-muted-foreground" />
          Settings
        </CommandItem>
      </CommandGroup>
    </>
  );

  return (
    <CommandDialog open={isOpen} onOpenChange={(open) => !open && closeModal()}>
      <CommandInput
        value={query}
        onValueChange={setQuery}
        placeholder="Search records, pages, and actions..."
      />
      <CommandList>
        <CommandEmpty>
          {searchMode && !searchLoading ? `No results for "${trimmedQuery}".` : 'No results found.'}
        </CommandEmpty>

        {!searchMode && recents.length > 0 && (
          <>
            <CommandGroup heading="Recent">
              {recents.map((record) => (
                <SearchResultItem
                  key={`${record.kind}:${record.id}`}
                  record={{ ...record, icon: Clock }}
                  onSelect={() => navigateRecord(record)}
                />
              ))}
            </CommandGroup>
            {canRenderNavigation && <CommandSeparator />}
          </>
        )}

        {searchMode ? (
          <>
            {searchResults.map((group) => renderSearchGroup(group))}
            {canRenderNavigation && hasSearchResults && <CommandSeparator />}
            {/* NOT-567 — always render the nav groups in search mode so cmdk can
                text-match pages by label + aliases (e.g. "call logs" → Calls,
                "requests" → the moved page), not only when a server record also
                matched. cmdk hides the non-matching items and empty groups. */}
            {canRenderNavigation ? renderNavigation() : null}
          </>
        ) : (
          renderNavigation()
        )}
      </CommandList>
    </CommandDialog>
  );
}
